import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import { DashboardLayout } from "components/DashboardLayout";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import Loader from "components/DesignSystem/Loader/loader";
import ProgressBar from "components/DesignSystem/ProgressBar/ProgressBar";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { PdfFile } from "components/icons/PdfFile";
import { PlusIcon } from "components/icons/PlusIcon";
import { MagicWand } from "components/icons/MagicWand";
import { SmallPencil } from "components/icons/SmallPencil";
import { EmptyInvoiceList } from "components/Illustrations/EmptyInvoiceList";
import { InvoiceActions } from "components/InvoiceAction/InvoiceAction";
import { InvoiceAmountColumn } from "components/InvoiceColumns/InvoiceColumns";
import { InvoiceCustomerModal } from "components/InvoiceCustomerModal/InvoiceCustomerModal";
import { SettingsRedirectConfirmation } from "components/InvoiceList/InvoiceList";
import { InvoiceSlider } from "components/InvoiceList/InvoiceSlider";
import { LockedUi } from "components/LockedUi/LockedUi";
import { DocumentPreviewModal } from "components/PreviewModal";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import UploadSimple from "static/images/UploadSimple.svg";
import { colors } from "constants/colors";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useCreateMessageDraftMutation } from "store/apis/chat";
import {
  CLICKED_EDIT_PENCIL_ICON_ON_CUSTOMER_DETAILS_CARD,
  INVOICE_ADD_CLICKED_WITH_CUSTOMER,
} from "constants/analyticsEvents";
import { DISPLAY_DATE } from "constants/date";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_OPTIONS,
} from "constants/revenueDataSource";
import { CUSTOMER_INFO_TAB } from "constants/invoicing";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAnalytics } from "hooks/useAnalytics";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDocPreview } from "hooks/useDocPreview";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ComponentProps, MouseEvent, useMemo, useState } from "react";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import RecurringIcon from "static/images/Recurring.svg";
import {
  useCreateEntityInvoiceMutation,
  useGetAllInvoiceCustomersQuery,
  useGetInvoiceCustomerQuery,
  useGetInvoiceSettingsQuery,
  useUpdateInvoiceMutation,
  useLazyExportOutstandingInvoicesQuery,
} from "store/apis/invoices";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { openLink } from "utils/openLink";

import { formatDate } from "utils/formatDate";

import {
  InvoiceFullPageView,
  InvoiceStatus,
} from "../Invoicing/InvoiceFullPageView";
import { Source } from "./InvoiceCustomers";
import { Invoice } from "types/Models/invoice";
import { CustomerDefaultPrices } from "./CustomerDefaultPrices";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { CreditNotes } from "./CreditNotes";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";

dayjs.extend(relativeTime);

const AddInvoiceWithBreadCrumbs = ({ url }: { url: string }) => {
  const { customerId } = useParams<{ customerId: string; invoiceId: string }>();
  const group = useCurrentGroupContext();
  const { search } = useLocation();
  const entityId = useCurrentEntityId();
  const history = useHistory();

  const { data: allCustomers, isLoading } = useGetAllInvoiceCustomersQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      customerId,
    },
    { skip: !group?.uuid || !entityId || !customerId }
  );

  const customer = allCustomers?.recipients?.find((r) => r.uuid === customerId);

  if (isLoading || !customer) {
    return (
      <div className="t-flex t-w-full t-h-full t-justify-center t-items-center">
        <Loader size="small" />
      </div>
    );
  }

  return <InvoiceFullPageView close={() => history.push(`${url}${search}`)} />;
};

const createColumn = createColumnHelper<Invoice>();

const InvoiceNumber = (
  props: CellContext<Invoice, Invoice["invoice_number"]>
) => {
  const openPreview = useDocPreview();
  const { url } = useRouteMatch();
  const docId = props.row.original.document?.file_id;
  const invoiceNumber = props.getValue();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const isStripeInvoice =
    props.row.original.source === REVENUE_DATA_SOURCE.STRIPE;

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    history.push(link(`${url}/invoice/${props.row.original.uuid}`));
    e.stopPropagation();
  };

  return (
    <>
      <div className="t-flex t-items-center t-gap-1">
        {isStripeInvoice ? (
          <Button customType="ghost">{invoiceNumber || "-"}</Button>
        ) : (
          <Button
            customType={invoiceNumber ? "link" : "ghost"}
            onClick={onClick}
          >
            {invoiceNumber || "-"}
          </Button>
        )}

        {docId && (
          <Button
            size="small"
            customType="ghost_icon"
            onClick={(e) => {
              e.stopPropagation();
              openPreview(docId);
            }}
          >
            <span className="t-text-text-30">
              <PdfFile />
            </span>
          </Button>
        )}
      </div>
    </>
  );
};

export const InvoiceColumns = [
  createColumn.accessor("invoice_number", {
    header: "Invoice Number",
    id: "INVOICE_NUMBER",
    size: 20,
    cell: InvoiceNumber,
    enableSorting: true,
  }),

  createColumn.accessor("invoice_date", {
    header: "Invoice date",
    id: "INVOICE_DATE",
    size: 20,
    cell: (info) => {
      const value = info.getValue();
      const invoiceRecurring = info.row.original.invoice_settings;

      return (
        <div>
          {value ? (
            <div className="t-flex t-gap-2 t-items-center">
              <ToolTip text={dayjs(value).format("MMM DD, YYYY, hh:mm A")}>
                <span className="t-text-body-sm t-font-medium">
                  {dayjs(value).format(DISPLAY_DATE)}
                </span>
              </ToolTip>
              {invoiceRecurring?.is_recurring_enabled && (
                <ToolTip
                  text={`Next invoice on ${formatDate(
                    invoiceRecurring.next_recurring_date
                  )}`}
                >
                  <span>
                    <img src={RecurringIcon} alt="RecurringIcon" />
                  </span>
                </ToolTip>
              )}
            </div>
          ) : (
            "-"
          )}
        </div>
      );
    },
    enableSorting: true,
  }),

  InvoiceAmountColumn,

  createColumn.accessor("status", {
    header: "Status",
    size: 5,
    cell: (info) => (
      <span className="t-flex t-text-body-sm">
        <InvoiceStatus
          invoiceStatus={info.getValue()}
          pendingAmount={info.row.original.pending_amount}
        />
      </span>
    ),
    enableSorting: false,
  }),

  createColumn.accessor("source", {
    size: 10,
    header: () => (
      <div className="t-flex t-justify-center t-w-full">SOURCE</div>
    ),
    cell: (info) => {
      const { source } = info.row.original;

      return <Source source={source} />;
    },
    enableSorting: false,
  }),

  createColumn.display({
    id: "Actions",
    header: "",
    size: 5,
    cell: (info) => <InvoiceActions invoice={info.row.original} />,
  }),
];

export const InvoiceCustomerInfo = ({
  breadcrumbs,
}: {
  breadcrumbs: ComponentProps<typeof Header>["breadcrumbs"];
}) => {
  const { url, path } = useRouteMatch();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const group = useCurrentGroupContext();
  const { customerId: id } = useParams<{ customerId: string }>();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const goToSettings = useModal();
  const { trackEvent } = useAnalytics();
  const entityId = useCurrentEntityId();
  const [invoiceId, setInvoiceId] = useState<string>("");
  const { search } = useLocation();
  const invoiceSlider = useModal();

  const handleInvoiceRowClick = (uuid: string) => {
    setInvoiceId(uuid);
    invoiceSlider.open();
  };
  const { isCustomer, isAdmin } = useRoleBasedView();
  const currentGroup = useCurrentGroup(group?.uuid);
  const [displayCurrency, setDisplayCurrency] = useState<
    "INVOICE_CURRENCY" | "USD"
  >("INVOICE_CURRENCY");

  const [createUpdateDraft] = useCreateMessageDraftMutation();

  const [createInvoice, { isLoading: creatingInvoice }] =
    useCreateEntityInvoiceMutation();

  const { data: invoiceSetting } = useGetInvoiceSettingsQuery(
    {
      groupId: group?.uuid!,
      entityId,
    },
    { skip: !group?.uuid || !entityId }
  );

  const [sorting, setSorting] = useState<
    {
      desc: boolean;
      id: "INVOICE_NUMBER" | "INVOICE_DATE";
    }[]
  >([]);

  const sortCol = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc;
  const {
    data: customer,
    isLoading,
    isSuccess,
  } = useGetInvoiceCustomerQuery(
    {
      customerId: id,
      groupId: group?.uuid!,
      entityId: entityId!,
      sortCol: sortCol,
      sortOrder: sortOrder ? ("DSC" as const) : ("ASC" as const),
    },
    {
      skip: !group?.uuid || !entityId || !id,
    }
  );

  const activeTab = query.get("tab") || CUSTOMER_INFO_TAB.INVOICES;

  const onTabChange = (value: string) => {
    update({
      query: "tab",
      value: value,
    });
  };

  const tableData = useMemo(() => customer?.invoices, [customer?.invoices]);
  const [updateInvoice, { isLoading: updatingInvoice }] =
    useUpdateInvoiceMutation();

  const { alertToast, successToast } = useToast();
  const [exportOutstandingInvoices, { isFetching: exportingOutstanding }] =
    useLazyExportOutstandingInvoicesQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const handleExportOutstanding = async ({
    exportToEmail,
  }: {
    exportToEmail: boolean;
  }) => {
    if (!group?.uuid || !entityId) return;
    try {
      const result = await exportOutstandingInvoices({
        groupId: group.uuid,
        entityId: entityId,
        customerId: id,
        exportToEmail,
      }).unwrap();

      if (exportToEmail) {
        successToast({
          message:
            "Outstanding invoices export initiated. You will receive an email shortly",
        });
        return;
      }

      if (result.excel_document_id) {
        const { download_url } = await getPreviewUrl({
          groupId: group.uuid,
          fileId: result.excel_document_id,
        }).unwrap();
        openLink(download_url);
      }

      successToast({ message: "Outstanding invoices exported successfully" });
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Something went wrong",
      });
    }
  };

  const table = useReactTable({
    data: tableData || [],
    columns: InvoiceColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    // @ts-ignore
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    meta: {
      displayCurrency,
      setDisplayCurrency,
    },
  });

  const history = useHistory();
  const { link } = useConstructInternalLink();

  const onCreateInvoice = async () => {
    // trackEvent(CLICKED_NEW_INVOICE_INSIDE_CUSTOMER_VIEW);
    if (!invoiceSetting) {
      goToSettings.open();
      return;
    }

    trackEvent(INVOICE_ADD_CLICKED_WITH_CUSTOMER);

    if (group.uuid && invoiceSetting?.entity_id) {
      try {
        const invoice = await createInvoice({
          groupId: group?.uuid,
          entityId: invoiceSetting?.entity_id,
        }).unwrap();

        const updatedInvoice = await updateInvoice({
          groupId: group.uuid,
          entityId: invoiceSetting.entity_id,
          invoiceId: invoice.uuid,
          payload: {
            customer_id: customer?.uuid,
          },
        }).unwrap();

        history.push(
          link(`/books/invoices-and-customers/${updatedInvoice.uuid}`)
        );
      } catch (error) {}
    }
  };

  const handleCustomerEdit = () => {
    setCustomerId(id);
    trackEvent(CLICKED_EDIT_PENCIL_ICON_ON_CUSTOMER_DETAILS_CARD);
  };

  const isEditable = customer?.source === REVENUE_DATA_SOURCE.MANUAL;
  const isStripeCustomer = customer?.source === REVENUE_DATA_SOURCE.STRIPE;

  if (!customer) {
    return (
      <div className="t-flex t-h-full t-w-full t-justify-center t-items-center">
        <Loader size="small" />
      </div>
    );
  }

  const { totalAmount, dueAmount, paidAmount, percentPaid } = {
    totalAmount: customer.total_amount + customer.total_stripe_amount,
    dueAmount: customer.due_amount + customer.stripe_due_amount,
    paidAmount: customer.paid_amount + customer.paid_stripe_amount,
    percentPaid: customer.total_percent_paid,
  };

  const goToAiChat = async (message: string) => {
    const aiChannelId = currentGroup?.ai_channel_url;
    const chatBasePath = isAdmin ? "/admin/chat" : "/books/chat";
    const chatPath = aiChannelId
      ? link(`${chatBasePath}/${aiChannelId}`, { moreQuery: { ai: true } })
      : link(`${chatBasePath}/`, { moreQuery: { ai: true } });

    if (aiChannelId) {
      try {
        await createUpdateDraft({ channelUrl: aiChannelId, message }).unwrap();
      } catch (err) {
        alertToast({
          message:
            (err as { data?: { message?: string } })?.data?.message ??
            "Something went wrong",
        });
      }
    }

    history.push(chatPath);
  };

  const handleSmartCreateInvoice = () => {
    if (!invoiceSetting) {
      goToSettings.open();
      return;
    }
    void goToAiChat(`Create an invoice for ${customer.name}`);
  };

  const handleUploadInvoiceFromDocument = () => {
    if (!invoiceSetting) {
      goToSettings.open();
      return;
    }
    void goToAiChat(
      `Create invoice from uploaded document for ${customer.name}`
    );
  };

  return (
    <Switch>
      <Route exact path={`${path}/invoice/:invoiceId`}>
        <AddInvoiceWithBreadCrumbs url={url} />
      </Route>
      <Route path={path}>
        <DashboardLayout
          header={
            <Header
              title={customer.name}
              v2
              breadcrumbs={[...(breadcrumbs || [])]}
            />
          }
        >
          <div className="t-flex t-flex-col t-gap-6 t-relative t-pb-20">
            <div className="t-flex t-gap-2 sm:t-flex-col sm:t-gap-3">
              <div className="t-border t-border-solid t-border-neutral-0 t-p-5 sm:t-p-4 t-flex-1 t-w-1/2 sm:t-w-full t-relative t-rounded-lg">
                <div className="t-flex t-flex-col t-gap-4">
                  <div className="t-grid t-grid-cols-2 t-gap-4 sm:t-grid-cols-1 sm:t-pr-8">
                    <InfoItem label="Email">
                      {customer?.recipient_emails?.join(", ") ||
                        customer?.email ||
                        "-"}
                    </InfoItem>
                    <InfoItem label="Invoice Prefix">
                      {customer?.customer_prefix || "-"}
                    </InfoItem>
                  </div>

                  <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
                    <div className="t-absolute t-top-5 t-right-5 sm:t-top-3 sm:t-right-3">
                      <ConditionalToolTip
                        condition={
                          !isEditable &&
                          `Cannot edit ${
                            REVENUE_DATA_SOURCE_OPTIONS[customer.source]
                          } customer`
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            customType="ghost_icon"
                            onClick={handleCustomerEdit}
                            disabled={!isEditable}
                          >
                            <span
                              className={classNames("t-flex", {
                                "t-text-blue-70": isEditable,
                                "t-text-neutral-20": !isEditable,
                              })}
                            >
                              <SmallPencil color="currentColor" />
                            </span>
                          </Button>
                        </span>
                      </ConditionalToolTip>
                    </div>
                  </LockedUi>

                  <InfoItem label="Address">
                    {customer.billing_address ? (
                      <span>{customer.billing_address}</span>
                    ) : (
                      "-"
                    )}
                  </InfoItem>
                  <InfoItem label="Reminders">
                    {customer.is_reminders_enabled ? "On" : "Off"}
                  </InfoItem>
                  <InfoItem label="CC Emails">
                    {customer?.cc_recipient_emails?.join(", ") || "-"}
                  </InfoItem>
                </div>
              </div>
              <div className="t-border t-border-solid t-border-neutral-0 t-p-5 sm:t-p-4 t-flex-1 t-w-1/2 sm:t-w-full t-rounded-lg">
                <div className="t-gap-y-6 t-flex-col t-flex">
                  <InfoItem label="Total invoice amount">
                    <span className="t-text-subtext">
                      <AmountSuperScript amount={totalAmount} />
                    </span>
                  </InfoItem>

                  <ProgressBar.Root
                    type="green"
                    disabled={customer.total_percent_paid <= 0}
                    className="t-h-4 t-flex t-items-center t-rounded-full"
                  >
                    <ProgressBar.Indicator
                      progress={percentPaid}
                      className="t-rounded-full t-flex t-items-center"
                      style={{ width: `${percentPaid}%` }}
                    >
                      <div className=" t-w-full t-flex t-justify-center t-text-caption t-items-center t-text-neutral-0">
                        <span>{percentPaid.toFixed(0)}%</span>
                      </div>
                    </ProgressBar.Indicator>
                  </ProgressBar.Root>

                  {
                    <div className="t-flex t-gap-9">
                      <ConditionalToolTip
                        condition={
                          <>
                            <li className="t-text-start">
                              Inkle:{" "}
                              <AmountSuperScript
                                amount={customer.paid_amount}
                              />{" "}
                              ({customer.percent_paid.toFixed(0)}%)
                            </li>
                            {isStripeCustomer && (
                              <li className="t-text-start">
                                Stripe:{" "}
                                <AmountSuperScript
                                  amount={customer.paid_stripe_amount}
                                />{" "}
                                ({customer.stripe_percent_paid.toFixed(0)}%)
                              </li>
                            )}
                          </>
                        }
                      >
                        <div className="t-flex t-gap-1 t-flex-col">
                          <Tag transparent tagType="green">
                            <span className="t-text-text-30">Paid</span>
                          </Tag>
                          <div className="t-text-subtext t-text-text-60 t-ml-3.5">
                            <AmountSuperScript amount={paidAmount} />
                          </div>
                        </div>
                      </ConditionalToolTip>

                      <ConditionalToolTip
                        condition={
                          <>
                            <li className="t-text-start">
                              Inkle:{" "}
                              <AmountSuperScript amount={customer.due_amount} />{" "}
                              ({(100 - customer.percent_paid).toFixed(0)}%)
                            </li>
                            {isStripeCustomer && (
                              <li className="t-text-start">
                                Stripe:{" "}
                                <AmountSuperScript
                                  amount={customer.stripe_due_amount}
                                />{" "}
                                (
                                {(100 - customer.stripe_percent_paid).toFixed(
                                  0
                                )}
                                %)
                              </li>
                            )}
                          </>
                        }
                      >
                        <div className="t-flex t-gap-1 t-flex-col">
                          <Tag transparent tagType="light_grey">
                            <span className="t-text-text-30">Due</span>
                          </Tag>
                          <div className="t-text-subtext t-text-text-60 t-ml-3.5">
                            <AmountSuperScript amount={dueAmount} />
                          </div>
                        </div>
                      </ConditionalToolTip>
                    </div>
                  }
                </div>
              </div>
            </div>

            <Tab.Root value={activeTab} onValueChange={onTabChange}>
              <div className="t-flex t-justify-between t-items-center t-border-b t-border-solid t-border-neutral-0 t-border-0">
                <Tab.List className="sm:!t-gap-5 sm:!t-mb-0 sm:!t-border-b-0">
                  <Tab.Trigger value={CUSTOMER_INFO_TAB.INVOICES}>
                    Invoices
                  </Tab.Trigger>
                  <Tab.Trigger value={CUSTOMER_INFO_TAB.DEFAULT_PRICES}>
                    Default prices
                  </Tab.Trigger>
                  <Tab.Trigger value={CUSTOMER_INFO_TAB.CREDIT_NOTES}>
                    Credit Notes
                  </Tab.Trigger>
                </Tab.List>
              </div>

              <Tab.Content
                value={CUSTOMER_INFO_TAB.INVOICES}
                className="t-pt-4"
              >
                <Async.Root
                  isLoading={isLoading}
                  isSuccess={isSuccess}
                  isEmpty={customer?.invoices.length === 0}
                >
                  <Async.Empty>
                    <>
                      <div className="t-flex t-justify-end t-pb-4">
                        <LockedUi
                          feature={WRITE_PERMISSION_KEYS.REVENUE}
                          blockedUi={<LockedButton text="Create invoice" />}
                        >
                          <Button
                            customType="primary"
                            onClick={onCreateInvoice}
                            size="small"
                            isLoading={creatingInvoice || updatingInvoice}
                            disabled={creatingInvoice || updatingInvoice}
                          >
                            Create invoice
                          </Button>
                        </LockedUi>
                      </div>
                      <div className="t-flex t-flex-col t-items-center t-justify-center t-h-72 t-text-text-60 t-gap-3">
                        <EmptyInvoiceList />
                        <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                          <p className="t-m-0 t-text-subtitle-sm">
                            Create your first invoice for {customer.name}
                          </p>
                          <p className="t-m-0 t-text-body-sm">
                            Send customised invoices to {customer.name}.
                          </p>
                        </div>
                        <LockedUi
                          feature={WRITE_PERMISSION_KEYS.REVENUE}
                          blockedUi={<LockedButton text="Create invoice" />}
                        >
                          <Button
                            onClick={onCreateInvoice}
                            isLoading={creatingInvoice || updatingInvoice}
                            disabled={creatingInvoice || updatingInvoice}
                            size="small"
                            customType="primary"
                          >
                            Create invoice
                          </Button>
                        </LockedUi>
                      </div>
                    </>
                  </Async.Empty>
                  <Async.Success>
                    <div className="t-flex t-justify-end t-pb-4 t-gap-2 sm:t-flex-wrap sm:t-justify-start">
                      <LockedUi
                        feature={WRITE_PERMISSION_KEYS.REVENUE}
                        blockedUi={<LockedButton text="New invoice" />}
                      >
                        <>
                          <Dropdown.Root>
                            <Dropdown.Trigger asChild>
                              <div>
                                <Button
                                  customType="secondary"
                                  size="small"
                                  isLoading={exportingOutstanding}
                                  disabled={exportingOutstanding}
                                >
                                  <div className="t-flex t-items-center t-gap-1.5">
                                    Export outstanding invoices
                                    <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                                      <ArrowDown color="currentColor" />
                                    </div>
                                  </div>
                                </Button>
                              </div>
                            </Dropdown.Trigger>
                            <Dropdown.Portal>
                              <Dropdown.Content sideOffset={4} align="end">
                                <ExportOptions
                                  onLocalDownload={() =>
                                    handleExportOutstanding({
                                      exportToEmail: false,
                                    })
                                  }
                                  onMailExport={() =>
                                    handleExportOutstanding({
                                      exportToEmail: true,
                                    })
                                  }
                                />
                              </Dropdown.Content>
                            </Dropdown.Portal>
                          </Dropdown.Root>
                          <Dropdown.Root>
                            <Dropdown.Trigger asChild className="t-group">
                              <div>
                                <Button
                                  customType="primary"
                                  size="small"
                                  isLoading={creatingInvoice || updatingInvoice}
                                  disabled={creatingInvoice || updatingInvoice}
                                >
                                  <div className="t-flex t-items-center t-gap-1.5">
                                    New invoice
                                    <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-text-surface">
                                      <ArrowDown
                                        color="currentColor"
                                        strokeWidth="1.5"
                                      />
                                    </div>
                                  </div>
                                </Button>
                              </div>
                            </Dropdown.Trigger>
                            <Dropdown.Portal>
                              <Dropdown.Content
                                sideOffset={8}
                                side="bottom"
                                className="t-min-w-48"
                                align="end"
                              >
                                {(isCustomer || isAdmin) && (
                                  <>
                                    <Dropdown.Item
                                      onSelect={handleSmartCreateInvoice}
                                      className="t-px-3 t-py-2 t-w-full"
                                      icon={
                                        <MagicWand color={colors.text.black} />
                                      }
                                    >
                                      <div className="t-flex t-items-center t-justify-between t-w-full">
                                        <span>Smart create</span>
                                        <div className="t-flex t-items-center t-gap-1 t-bg-purple-0 t-text-purple-600 t-text-xs t-px-2 t-rounded-full t-text-body-sm">
                                          <img
                                            src={AIGenerateButtonStar}
                                            alt="AI star"
                                            className="t-w-3 t-h-3"
                                          />
                                          AI
                                        </div>
                                      </div>
                                    </Dropdown.Item>

                                    <Dropdown.Item
                                      onSelect={handleUploadInvoiceFromDocument}
                                      className="t-px-3 t-py-2 t-w-full"
                                      icon={
                                        <img
                                          src={UploadSimple}
                                          alt="UploadSimple"
                                          className="t-size-4"
                                        />
                                      }
                                    >
                                      <div className="t-flex t-items-center t-justify-between t-w-full">
                                        <span>Upload file</span>
                                        <div className="t-flex t-items-center t-gap-1 t-bg-purple-0 t-text-purple-600 t-text-xs t-px-2 t-text-body-sm t-rounded-full">
                                          <img
                                            src={AIGenerateButtonStar}
                                            alt="AI star"
                                            className="t-w-3 t-h-3"
                                          />
                                          AI
                                        </div>
                                      </div>
                                    </Dropdown.Item>
                                  </>
                                )}

                                <Dropdown.Item onSelect={onCreateInvoice}>
                                  <div className="t-flex t-items-center t-gap-1">
                                    <PlusIcon size="12" />
                                    <div>Create an Invoice</div>
                                  </div>
                                </Dropdown.Item>
                              </Dropdown.Content>
                            </Dropdown.Portal>
                          </Dropdown.Root>
                        </>
                      </LockedUi>
                    </div>
                    <TableUI
                      table={table}
                      onRowClick={(row) =>
                        handleInvoiceRowClick(row.original.uuid)
                      }
                      enableSort
                    />
                  </Async.Success>
                </Async.Root>
                <Slider.Root
                  open={invoiceSlider.isOpen}
                  onOpenChange={invoiceSlider.close}
                >
                  <Slider.Content open={invoiceSlider.isOpen}>
                    <InvoiceSlider
                      invoiceId={invoiceId}
                      redirectLink={`${url}/invoice/${invoiceId}${search}`}
                    />
                  </Slider.Content>
                </Slider.Root>
              </Tab.Content>

              <Tab.Content
                value={CUSTOMER_INFO_TAB.DEFAULT_PRICES}
                className="t-pt-4"
              >
                <CustomerDefaultPrices customerId={id} entityId={entityId!} />
              </Tab.Content>
              <CreditNotes
                customerName={customer.name}
                customerId={customer.uuid}
              />
            </Tab.Root>
          </div>
          <InvoiceCustomerModal
            customerId={customerId}
            setCustomerId={setCustomerId}
          />
          <SettingsRedirectConfirmation
            isOpen={goToSettings.isOpen}
            close={goToSettings.close}
            url={`/books/invoices-and-customers/settings${search}`}
          />
        </DashboardLayout>
        <DocumentPreviewModal groupId={group.uuid} />
      </Route>
    </Switch>
  );
};
